import { ReactElement } from "react";
import { Trans, useTranslation } from "next-i18next";
import Text from "@ui-kit/typography/Text/Text.web";
import { TicketBasketType } from "@shared/modules/basket/types";
import { enums } from "@shared/constants";
import StaticAlert from "@ui-kit/feedback/alerts/StaticAlert/StaticAlert.web";
import Icon from "@ui-kit/icons/Icon.web";

export type CartDiscountMessageProps = {
  discountMessageObject: TicketBasketType["discount_message_object"];
  isTransparent?: boolean;
};

export default function CartDiscountMessage({
  discountMessageObject,
  isTransparent = false,
}: CartDiscountMessageProps): ReactElement | null {
  const { t } = useTranslation("purchases");
  const {
    discount_amount,
    discount_percentage,
    currency_symbol,
    required_need,
    discount_apply_method,
    limit_type,
  } = discountMessageObject || {};

  const getDiscountValueText = () => {
    if (discount_amount) {
      return `${currency_symbol}${discount_amount.toFixed(2)}`;
    }
    return `${discount_percentage}%`;
  };

  const getDiscountApplicationText = () => {
    if (discount_apply_method === enums.APPLY_TYPE_MAP.APPLY_TO_EACH.id) {
      return t("off these items!");
    }
    if (discount_apply_method === enums.APPLY_TYPE_MAP.APPLY_ONCE.id) {
      return t("off your order!");
    }
    return "";
  };

  const getMessage = () => {
    const discountValueText = getDiscountValueText();
    const discountApplicationText = getDiscountApplicationText();

    if (
      limit_type ===
      enums.DISCOUNT_LIMIT_TYPE_MAP.DISC_LIMIT_TYPE_TOTAL_PURCHASE_AMOUNT.id
    ) {
      return (
        <Trans
          t={t}
          components={{ strong: <strong /> }}
          i18nKey="Spend an additional {{currency_symbol}}{{required_need}} for a <strong>{{discountValueText}}</strong> discount {{discountApplicationText}}"
          values={{
            currency_symbol,
            required_need,
            discountValueText,
            discountApplicationText,
          }}
        />
      );
    }
    if (
      limit_type ===
      enums.DISCOUNT_LIMIT_TYPE_MAP.DISC_LIMIT_TYPE_TOTAL_BASKET_QUANTITY.id
    ) {
      return (
        <Trans
          t={t}
          components={{ strong: <strong /> }}
          i18nKey="Add <strong>{{required_need}}</strong> more items to your basket for a <strong>{{discountValueText}}</strong> discount {{discountApplicationText}}"
          values={{
            required_need,
            discountValueText,
            discountApplicationText,
          }}
        />
      );
    }

    return null;
  };

  const message = getMessage();

  if (!message) {
    return null;
  }

  return (
    <StaticAlert
      size="sm"
      status="success"
      icon={<Icon.LightningBolt />}
      isTransparent={isTransparent}
      title={<Text fontWeight="normal">{message}</Text>}
    />
  );
}
